import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import ReturnButton from '../../components/ui/ReturnButton';
import RouteDescription from './sections/DescriptionSection';
import RouteDetails from './sections/DetailsSection';
import RoutePoints from './sections/PointSection';
import RouteMap from '../../components/Map/RouteMap';
import Header from '../../components/layout/Header/Header';
import Footer from '../../components/layout/Footer/Footer';

const RoutePage = () => {
  const { id } = useParams();
  console.log('ID из URL:', id);

  const [route, setRoute] = useState(null);
  const [points, setPoints] = useState([]);
  const [additionalInfo, setAdditionalInfo] = useState([]);
  const [routeStats, setRouteStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const apiUrl = process.env.REACT_APP_API_URL;

        const [routeResponse, pointsResponse, infoResponse] = await Promise.all([
          fetch(`${apiUrl}/routes/${id}`),
          fetch(`${apiUrl}/routes/${id}/points`),
          fetch(`${apiUrl}/routes/${id}/additional-info`)
        ]);

        if (!routeResponse.ok) {
          throw new Error('Маршрут не найден');
        }

        const routeData = await routeResponse.json();
        
        let pointsData = [];
        if (pointsResponse.ok) {
          const pointsRaw = await pointsResponse.json();
          pointsData = Array.isArray(pointsRaw) ? pointsRaw : [];
        }
        
        let infoList = [];
        let statsData = null;
        
        if (infoResponse.ok) {
          const rawInfo = await infoResponse.json();
          console.log('Raw дополнительная информация:', rawInfo);
          
          if (Array.isArray(rawInfo)) {
            rawInfo.forEach(item => {
              const isStatItem = item.title === "distance" || 
                                 item.title === "time" || 
                                 item.title === "steps";
              
              if (isStatItem) {
                if (!statsData) statsData = {};
                statsData[item.title] = item.description;
              } else {
                infoList.push(item);
              }
            });
          } else if (typeof rawInfo === 'object' && rawInfo !== null) {
            if (rawInfo.distance || rawInfo.time || rawInfo.steps) {
              statsData = {
                distance: rawInfo.distance,
                time: rawInfo.time,
                steps: rawInfo.steps
              };
            }
            Object.keys(rawInfo).forEach(key => {
              if (!['distance', 'time', 'steps'].includes(key) && rawInfo[key]) {
                infoList.push({ title: key, description: rawInfo[key] });
              }
            });
          }
        }
        
        if (!statsData && (routeData.distance || routeData.time || routeData.steps)) {
          statsData = {
            distance: routeData.distance,
            time: routeData.time,
            steps: routeData.steps
          };
        }

        if (pointsData.length > 0) {
          const pointsWithPhotos = await Promise.all(
            pointsData.map(async (point) => {
              try {
                const photosResponse = await fetch(`${apiUrl}/points/${point.id}/photos`);
                
                if (photosResponse.ok) {
                  const photosRaw = await photosResponse.json();
                  let photosData = Array.isArray(photosRaw) ? photosRaw : (photosRaw.data || []);
                  
                  const formattedPhotos = photosData.map(photo => ({
                    id: photo.id,
                    link: photo.link || photo.url,
                    url: photo.url || photo.link,
                    sort_order: photo.sort_order
                  }));
                  
                  formattedPhotos.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
                  
                  return { 
                    ...point, 
                    photos: formattedPhotos,
                    photoLinks: formattedPhotos.map(p => p.link)
                  };
                }
              } catch (err) {
                console.warn(`Ошибка загрузки фото для точки ${point.id}:`, err);
              }
              return { ...point, photos: [], photoLinks: [] };
            })
          );
          setPoints(pointsWithPhotos);
        } else {
          setPoints([]);
        }

        setRoute(routeData);
        setAdditionalInfo(infoList);
        setRouteStats(statsData);
      
      } catch (err) {
        console.error('Ошибка:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchData();
    }
  }, [id]);

  const hasDescription = () => {
    return route?.description && route.description.trim() !== '';
  };

  if (loading) return <div>Загрузка маршрута {id}...</div>;
  if (error) return <div>Ошибка: {error}</div>;
  if (!route) return <div>Маршрут с ID {id} не найден</div>;
  
  const paragraphs = route.description
    ? route.description.split(/\r?\n|\\n/).filter(p => p.trim() !== '')
    : [];

  const chunkSize = Math.ceil(paragraphs.length / 3);

  const textBlocks = [
    paragraphs.slice(0, chunkSize),
    paragraphs.slice(chunkSize, chunkSize * 2),
    paragraphs.slice(chunkSize * 2),
  ];

  const getMapPoints = () => {
    if (!points || points.length === 0) return [];
    
    const sortedPoints = [...points].sort((a, b) => {
      const orderA = a.sort_order !== undefined ? a.sort_order : 0;
      const orderB = b.sort_order !== undefined ? b.sort_order : 0;
      return orderA - orderB;
    });
    
    return sortedPoints.map(point => ({
      coords: [point.latitude, point.longitude],
      title: point.name,
      description: point.description
    }));
  };

  const mapPoints = getMapPoints();
  const hasDesc = hasDescription();

  const RouteHeader = () => (
    <div className="routeNameWrapper">
      <div className="routeNameInner">
        <h1 className="routeName">{route.name}</h1>
        <div className="routeNameYellowUnderneath"></div>
      </div>
    </div>
  );

  return (
  <div id="container" className="flex-column route-page"> 
    <Header />
    <ReturnButton />

    <div className="desktop-layout">
      {hasDesc ? (
        <RouteDescription 
          name={route.name}
          description={route.description}
        />
      ) : (
        <div className="routeInfoWrapper">
          <RouteHeader />
        </div>
      )}
      
      <RouteMap points={mapPoints} />
      
      {hasDesc && (
        <RouteDetails details={additionalInfo} routeStats={routeStats} />
      )}
      
      {hasDesc && points.length > 0 && <RoutePoints points={points} />}
    </div>

      {/* Мобильная версия */}
      {/* {hasDesc && (
        <div className="mobile-layout">
          <div className="mobile-block">
            <div className="routeNameWrapper mobile-route-name-wrapper">
              <div className="routeNameInner">
                <h1 className="routeName">{route.name}</h1>
                <div className="routeNameYellowUnderneath"></div>
              </div>
            </div>

            <div className="mobile-text">
              <div className="mobile-desc">
                {textBlocks[0]?.map((p, i) => <p key={i}>{p}</p>)}
              </div>
            </div>

            {points[0] && points[0].photos?.[0] && (
              <div className="mobile-point-card">
                <h4>{points[0].name}</h4>
                <img 
                  src={points[0].photos[0].link || points[0].photos[0].url} 
                  alt={points[0].name}
                  className="mobile-photo"
                />
              </div>
            )}
          </div>

          <div className="mobile-block">
            <div className="mobile-text">
              <div className="mobile-desc">
                {textBlocks[1]?.map((p, i) => <p key={i}>{p}</p>)}
              </div>
            </div>

            {points[1] && points[1].photos?.[0] && (
              <div className="mobile-point-card">
                <h4>{points[1].name}</h4>
                <img 
                  src={points[1].photos[0].link || points[1].photos[0].url} 
                  alt={points[1].name}
                  className="mobile-photo"
                />
              </div>
            )}
          </div>

          <div className="mobile-block">
            <div className="mobile-text">
              <div className="mobile-desc">
                {textBlocks[2]?.map((p, i) => <p key={i}>{p}</p>)}
              </div>
            </div>

            {points[2] && points[2].photos?.[0] && (
              <div className="mobile-point-card">
                <h4>{points[2].name}</h4>
                <img 
                  src={points[2].photos[0].link || points[2].photos[0].url} 
                  alt={points[2].name}
                  className="mobile-photo"
                />
              </div>
            )}
          </div>
        </div>
      )} */}

      <Footer />
    </div>
  );
};

export default RoutePage;